/**
 * **상품 BFF 질문** — 우리 `/api/products/*` 를 지나 외부 상품 백엔드로 나가는 셋.
 *
 * ```
 *   sections/market/…             취소 · 로딩 · 캐시 (여기 없다)
 *     │ searchLiveProducts(params)
 *   이 파일                        질문 — 주소와 응답 모양을 안다
 *     ▼ /api/products/{live-search,amazon-detail,collect-detail}
 *   외부 상품 백엔드                답한다
 * ```
 *
 * ⚠️ **랭킹 인덱스 질문은 여기가 아니다** — `actions/els/product-rankings`(ES) 다. 같은 화면이
 * 묻지만 데이터소스가 다르다.
 * ⚠️ 셋 다 **로그인 토큰이 필수다** — 관문 뒤라 헤더를 빼면 401 이다.
 * ⚠️ 첫 줄의 `resolveMock*()` 는 mockup 라우트가 등록했을 때만 값을 준다(`product-mock.ts`).
 *    등록이 없으면 늘 `null` 이라 실화면은 평소 경로 그대로다.
 */

import { bearerAuthHeader } from 'src/lib/firebase/auth-kit';

import {
  resolveMockLiveProductSearch,
  resolveMockAmazonProductGallery,
  resolveMockProductDetailCollect,
} from './product-mock';

/** 판매몰 실시간 검색이 묻는 것 — ⚠️ `limit` 상한은 50 이다(BFF·상류가 함께 자른다) */
export type LiveProductSearchRequest = {
  query: string;
  limit: number;
};

/** 실시간 검색이 돌려주는 한 건 — 서버가 모양을 정하므로 느슨하게 받는다 */
export type LiveProductSearchItem = {
  source?: string;
  /** BFF 가 채널로 정해 실어 준다 — 국내는 `KRW`, 아마존은 `USD` */
  currency?: string;
  id?: string;
  title?: string;
  /** 몰이 적어 준 **원문** */
  brand?: string;
  /**
   * **정규화된 브랜드** — 색인(`canonicalBrand`)과 **같은 규칙**으로 상류가 실어 준다.
   * ⚠️ 못 붙이면 **없다** — 원문을 억지로 넣지 않는다(색인과 같은 약속).
   */
  canonicalBrand?: string;
  image?: string;
  /** 대표 컷 여러 장. 몰에 따라 안 올 수도 있다 */
  images?: string[];
  link?: string;
  originalPrice?: number | null;
  price?: number | null;
  displayPrice?: number | null;
  rating?: number | null;
  reviewCount?: number | null;
  ranked?: boolean;
  matchedBy?: string | null;
  /** 랭킹 인덱스와 이어졌으면 그쪽 스냅샷이 통째로 온다 */
  ranking?: Record<string, unknown> | null;
};

/**
 * 실시간 검색 응답.
 *
 * ⚠️ `configured: false` 는 **오류가 아니라 「백엔드가 아직 안 붙었다」**다 — 부르는 쪽이
 * `note` 를 안내로 띄운다.
 */
export type LiveProductSearchResponse = {
  configured?: boolean;
  note?: string;
  items?: LiveProductSearchItem[];
  /** 몰별 건수 */
  counts?: Record<string, number>;
  errors?: Record<string, unknown> | null;
  /** 랭킹 인덱스와 이어진 건수 */
  rankedCount?: number;
};

/** 고시정보 한 줄 — 몰 상세페이지의 라벨·값 */
export type ProductNoticeItem = { key: string; label: string; value: string };

/** 아마존 단건에서 바로 읽어 오는 대표 컷과 고시정보 */
export type AmazonProductGallery = {
  images: string[];
  notice: ProductNoticeItem[];
};

/** 상세 수집이 상품 하나마다 흘리는 이벤트. 모양은 서버가 정하므로 느슨하게 받는다 */
export type ProductDetailCollectEvent = {
  status?: string;
  source?: string;
  productId?: string;
  [key: string]: unknown;
};

/**
 * **판매몰 실시간 검색** — 랭킹 인덱스 밖의 상품까지 그때 한 번 훑는다.
 *
 * @throws 상류가 실패하면 서버 문구(`error` · `detail`)를, 없으면 `요청 실패 (<status>)`
 */
export async function searchLiveProducts(
  params: LiveProductSearchRequest,
): Promise<LiveProductSearchResponse> {
  const mocked = resolveMockLiveProductSearch(params);
  if (mocked) return mocked;

  const response = await fetch(LIVE_SEARCH_PATH, {
    method: 'POST',
    // 실시간 검색(외부 상류)은 로그인 토큰 필수
    headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
    body: JSON.stringify(params),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.error || data?.detail || `요청 실패 (${response.status})`);
  return data;
}

/**
 * **아마존 단건 대표 컷·고시정보** — 색인 문서가 없는 상품만 부른다.
 *
 * ⚠️ 상태 코드를 보지 않는다 — 실패도 빈 벌로 떨어진다. 비교 테이블의 보조 칸이라
 * 오류로 화면을 멈추지 않는다.
 */
export async function fetchAmazonProductGallery(asin: string): Promise<AmazonProductGallery> {
  const mocked = resolveMockAmazonProductGallery(asin);
  if (mocked) return mocked;

  // 아마존 상세(외부 상류)는 로그인 토큰 필수 — 형제 라우트(live-search·collect)와 같은 관문
  const res = await fetch(`${AMAZON_DETAIL_PATH}?asin=${encodeURIComponent(asin)}`, {
    headers: { ...(await bearerAuthHeader()) },
  });
  const data = await res.json().catch(() => ({}));
  return {
    images: Array.isArray(data?.images) ? (data.images as string[]) : [],
    notice: Array.isArray(data?.notice) ? (data.notice as ProductNoticeItem[]) : [],
  };
}

/**
 * **상세 수집 SSE 스트림** — 상품을 하나씩 긁으며 진행 상황을 흘려 보낸다.
 *
 * 한 번에 여러 개를 넘기고 **끝난 것부터** 화면에 반영하려는 것이라 응답을 통째로 기다리지
 * 않는다. 진행률은 반환값이 아니라 `onEvent` 로 온다.
 *
 * @throws 시작에 실패하면 서버 문구(`error` · `detail`)를, 없으면 `요청 실패 (<status>)`
 */
export async function streamProductDetailCollect(
  items: unknown[],
  onEvent: (event: ProductDetailCollectEvent) => void,
): Promise<void> {
  const mocked = resolveMockProductDetailCollect(items);
  if (mocked) {
    mocked.events.forEach(onEvent);
    if (mocked.holdMs) await new Promise((resolve) => { setTimeout(resolve, mocked.holdMs); });
    return;
  }

  const response = await fetch(COLLECT_DETAIL_PATH, {
    method: 'POST',
    // 상세 수집(비용·최대 300초)은 로그인 토큰 필수
    headers: { 'Content-Type': 'application/json', ...(await bearerAuthHeader()) },
    body: JSON.stringify({ items }),
  });

  if (!response.ok || !response.body) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error || data?.detail || `요청 실패 (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // ⚠️ SSE는 빈 줄 둘(`\n\n`)이 이벤트 경계다. 청크가 그 경계에서 끊긴다는 보장이 없어
  //    버퍼에 모아 두고 경계가 나올 때마다 잘라 낸다
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const matched = chunk.match(/^data: (.*)$/m);
      if (matched) {
        try {
          onEvent(JSON.parse(matched[1]));
        } catch {
          /* 파싱 실패한 이벤트는 무시 — 한 건 때문에 스트림을 끊지 않는다 */
        }
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
}

// ----------------------------------------------------------------------

/** ⚠️ 주소는 여기 한 곳뿐이다 — 화면이 URL 을 조립하면 이 경계가 무너진다 */
const LIVE_SEARCH_PATH = '/api/products/live-search';
const AMAZON_DETAIL_PATH = '/api/products/amazon-detail';
const COLLECT_DETAIL_PATH = '/api/products/collect-detail';
